import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import MarketplaceShell from "./MarketplaceShell";

function hasAccessibleSalon(entry) {
  const salonId = Number(entry?.salon_id ?? entry?.id ?? entry?.salonId ?? null);
  return Number.isFinite(salonId) && salonId > 0;
}

export default function MarketplaceEllyannaAccessPage() {
  const brand = useBrand();
  const { user, accessibleSalons } = useAuth();
  const hasDirectOpsAccess =
    Boolean(user?.is_super_admin) ||
    Boolean(user?.salon_id) ||
    (Array.isArray(accessibleSalons) && accessibleSalons.some(hasAccessibleSalon));

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-[#2f3566] px-6 py-6 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
              Ellyanna Access
            </p>
            <h1 className="mt-3 text-3xl font-semibold">Get Ellyanna Booking Access</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-200 md:text-base">
              Your MyBoothShare account already uses the shared Ellyanna login system. To
              open Ellyanna Booking, this account still needs salon access or its own
              salon / independent professionals setup.
            </p>
          </div>
          <div className="grid gap-6 px-6 py-6 lg:grid-cols-[1.05fr,0.95fr]">
            <div className="space-y-6">
              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Current status
                </div>
                <div className="mt-3 text-2xl font-semibold text-slate-900">
                  {hasDirectOpsAccess ? "Access available" : "Marketplace-only access"}
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {hasDirectOpsAccess
                    ? "This login already has salon access. You can open Ellyanna Booking directly."
                    : "This login can use MyBoothShare today, but it does not yet have a salon or independent professionals context for Ellyanna Booking."}
                </p>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-600">
                  Shared email:
                  <span className="ml-2 font-semibold text-slate-900">
                    {user?.email || "Not available"}
                  </span>
                </div>
                {hasDirectOpsAccess ? (
                  <div className="mt-5">
                    <a
                      href={brand.opsHomeUrl}
                      className="inline-flex rounded-full bg-[#2f3566] px-5 py-3 text-sm font-semibold text-white hover:bg-[#252a53]"
                    >
                      Open Ellyanna Booking
                    </a>
                  </div>
                ) : null}
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Option 1
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Join an existing salon
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Use this when you want to operate under a host salon as a technician or
                  staff member. Once approved, that salon access will let you open the
                  Ellyanna Booking side.
                </p>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  You can search salons, request access, or use a direct invite link from a
                  salon owner or manager.
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to="/join-salon"
                    className="rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600"
                  >
                    Join A Salon
                  </Link>
                  <Link
                    to="/search"
                    className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Search Workspaces
                  </Link>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div
                id="start-your-own-salon"
                className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Option 2
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Start your own salon or independent professionals setup
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Use this when you want your own Ellyanna salon or independent professionals
                  setup. The same shared login is upgraded, then you continue into the
                  Ellyanna setup flow for a salon owner or independent technician.
                </p>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  The setup section lives in your MyBoothShare profile. After the
                  upgrade succeeds, you are redirected into Ellyanna setup.
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to="/account/profile#owner-upgrade"
                    className="rounded-full bg-[#2f3566] px-5 py-3 text-sm font-semibold text-white hover:bg-[#252a53]"
                  >
                    Start Salon / Independent Professionals Setup
                  </Link>
                  <Link
                    to="/account/profile"
                    className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Open MyBoothShare Profile
                  </Link>
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                Ellyanna Booking should only open once this account has a real salon or
                independent professionals context. Until then, stay on MyBoothShare for renter profile,
                search, checkout, bookings, and join requests.
              </div>
            </div>
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
